import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import AllDialogs from "../components/AllDialogs";

/**
 * Scrolls to the element named by the URL hash after each navigation
 * (BrowserRouter doesn't do this itself). Pages are lazy-loaded, so the
 * target may not be in the DOM yet when the location changes — retry on
 * animation frames (~1s) until the chunk mounts. Keyed on `location`, not
 * [pathname, hash], so re-clicking the same link scrolls again.
 * Hashless navigations scroll to the top — otherwise a cross-page click
 * from a scrolled position lands mid-page on the new route.
 */
function ScrollToHash() {
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    const id = location.hash.slice(1);
    let tries = 0;
    let raf;
    const attempt = () => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (tries++ < 60) {
        raf = requestAnimationFrame(attempt);
      }
    };
    attempt();
    return () => cancelAnimationFrame(raf);
  }, [location]);
  return null;
}

// Route-level code splitting. Each page becomes its own chunk so the
// initial bundle stays lean and three.js / drei only land in the user's
// browser when they navigate to a page that needs them.
const Home = lazy(() => import("../pages/Home"));
const Company = lazy(() => import("../pages/Company"));
const MyAccount = lazy(() => import("../pages/MyAccount"));
const Commons = lazy(() => import("../pages/Commons"));
const Standards = lazy(() => import("../pages/Standards"));
const Solutions = lazy(() => import("../pages/Solutions"));
const Explore = lazy(() => import("../pages/Commons/Explore"));
const AnalyzeDocument = lazy(() => import("../pages/AnalyzeDocument"));
const AlignStandards = lazy(() => import("../pages/AlignStandards"));

const RouteFallback = () => (
  <div className="loader-container" data-testid="route-loader">
    <div className="loader" />
  </div>
);

function MyRoutes() {
  return (
    <Router>
      <ScrollToHash />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<Company />} />
          <Route path="/platform" element={<Commons />} />
          <Route path="/standards" element={<Standards />} />
          <Route path="/solutions" element={<Solutions />} />
          <Route path="/services/analyze-document" element={<AnalyzeDocument />} />
          <Route path="/services/align-standards" element={<AlignStandards />} />
          <Route path="/explore" element={<Explore />} />
          <Route path="/myaccount" element={<MyAccount />} />
          {/* Unknown URLs otherwise render a blank page with no way back. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <AllDialogs />
    </Router>
  );
}

export default MyRoutes;
